import Checkbox from '@atlaskit/checkbox';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  getDatabaseWithCurrentView,
  selectFocusedItem,
  selectIsEntireEntrySelected,
  selectSelectedEntireEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { calculateRangeFromFocus } from '@databases/redux/cellSelections/utils';
import { getService } from '@databases/service-container';
import { useCallback } from 'react';
import React from 'react';

const EntryCheckbox = ({
  contentId,
  entryId,
  isSelected,
  referencing,
  index,
}: {
  contentId: string;
  entryId: string;
  isSelected: boolean;
  referencing: boolean;
  index: number;
}) => {
  const analyticsClient = useAnalytics();
  const store = defaultStoreUtils.useStore();
  const selectionsActions = getService('SelectionsActions');
  const databaseContainer = getDatabaseContainerType();

  /**
   * Selection logic moved from Redux thunk `selectEntryMulti`.
   *
   * - Shift+click (when not selected): Range select from focused entry to clicked entry
   * - Click when not selected: Add to selection
   * - Click when selected: Deselect by selecting remaining entries
   */
  const handleRowCheckboxSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.blur();
      if (!entryId) {
        return;
      }

      const state = store.getState();
      const isRangeSelectOn =
        isModifierKeyPressed(e.nativeEvent as MouseEvent | KeyboardEvent, ['shiftKey']) &&
        !isSelected;

      analyticsClient.sendRowCheckboxSelectedEvent({
        entryId,
        contentId,
        databaseContainer,
        checkboxState: isSelected ? 'unselected' : 'selected',
      });

      if (isRangeSelectOn) {
        const focusedItem = selectFocusedItem(state, { contentId, type: 'entry' });
        const database = getDatabaseWithCurrentView(state, { contentId });
        const entries = database?.entries ?? [];
        const rangeEntryIds = calculateRangeFromFocus(
          entries,
          focusedItem?.entryId,
          entryId
        );
        selectionsActions.selectEntries(rangeEntryIds, { clear: false });
      } else {
        const isCurrentlySelected = selectIsEntireEntrySelected(state, { entryId });
        if (isCurrentlySelected) {
          const currentEntries = selectSelectedEntireEntryIds(state, { contentId });
          const remainingEntries = currentEntries.filter((id) => id !== entryId);
          if (remainingEntries.length > 0) {
            selectionsActions.selectEntries(remainingEntries, {
              clearSelectedValues: false,
            });
          } else {
            selectionsActions.deselectAll();
          }
        } else {
          selectionsActions.selectEntries([entryId], { clear: false });
        }
      }
      selectionsActions.clearSelectionIntent();
    },
    [
      isSelected,
      entryId,
      analyticsClient,
      contentId,
      databaseContainer,
      store,
      selectionsActions,
    ]
  );

  return (
    <Checkbox
      onChange={handleRowCheckboxSelected}
      isChecked={isSelected}
      isDisabled={referencing}
      tabIndex={-1}
      className="row-checkbox"
      data-entry-id={entryId}
      data-row-index={index}
    />
  );
};
export default EntryCheckbox;
